<template>
  <div
    class="b-input-box"
    :class="classes"
  >
    <BInput
      v-if="inputComponent === 'BInput'"
      v-model="internalValue"
      v-model:valid="internalValid"
      class="input-area"
      :placeholder="placeholder"
      :rules="mergedRules"
      :disabled="loading"
      :max-length="maxLength"
      :data-test="dataTest"
      :type="type"
      :lowercase-email="lowercaseEmail"
      @keypress-enter="handleCheckWithValue"
      @blur="handleBlur"
    />
    <BNumberInput
      v-if="inputComponent === 'BNumberInput'"
      v-model="internalValue"
      class="number-input"
      :placeholder="$t('customField.number.validate')"
      :disabled="loading"
      :allow-float="allowFloat"
      @keypress-enter="handleCheck"
      @blur="handleBlur"
    />
    <div
      v-if="isSettleByCheck || closeable"
      class="btn-area"
    >
      <BLayout align-center>
        <template v-if="isSettleByCheck">
          <BBtn
            v-if="valueChanged"
            class="mr-50"
            size="small"
            fab
            flat
            :disabled="!internalValid"
            :loading="loading"
            :data-test="`${dataTest}Btn`"
            @click="handleCheck"
          >
            <BIcon
              class="check-icon"
              size="small"
            >
              check
            </BIcon>
          </BBtn>
        </template>
        <BBtn
          v-if="closeable"
          size="small"
          fab
          flat
          :disabled="loading"
          @click="handleClose"
        >
          <BIcon
            class="close-icon"
            size="small"
          >
            close
          </BIcon>
        </BBtn>
      </BLayout>
    </div>
  </div>
</template>

<script>
import { useInputValidations } from '@/composable/input-validations';

export default {
  props: {
    modelValue: [String, Number],
    placeholder: String,
    vert: Boolean,
    horiz: Boolean,
    required: Boolean,
    loading: Boolean,
    rules: {
      type: Array,
      default () {
        return [];
      },
    },
    maxLength: {
      type: Number,
      default: 255,
    },
    dataTest: String,
    type: {
      type: String,
      default: 'text',
    },
    inputComponent: {
      type: String,
      default: 'BInput',
    },
    allowFloat: {
      type: Boolean,
      default: false,
    },
    settleType: {
      type: String, // 'check' or 'blur'
      default: 'check',
    },
    closeable: {
      type: Boolean,
      default: true,
    },
    lowercaseEmail: Boolean,
  },
  emits: [
    'update:modelValue',
    'update:valid',
    'check',
    'close',
  ],
  setup() {
    const { requiredRule } = useInputValidations();
    return { requiredRule };
  },
  data () {
    return {
      valid: false,
      valueChanged: false,
    };
  },
  computed: {
    classes () {
      return {
        'b-input-box-horiz': this.horiz,
        'b-input-box-vert': this.vert,
      };
    },
    internalValue: {
      get () {
        return this.modelValue;
      },
      set (newVal) {
        if (this.modelValue !== newVal) {
          this.valueChanged = true;
          this.$emit('update:modelValue', newVal);
        }
      },
    },
    internalValid: {
      get() {
        return this.valid;
      },
      set(newVal) {
        this.valid = newVal;
        this.$emit('update:valid', newVal);
      },
    },
    mergedRules () {
      const array = [];
      if (this.required) array.push(this.requiredRule);
      return array.concat(this.rules);
    },
    isSettleByCheck() {
      return this.settleType === 'check';
    },
    isSettleByBlur() {
      return this.settleType === 'blur';
    },
  },
  created () {
    this.internalValid = this.inputComponent === 'BNumberInput';  // BNumberInputは内部でバリデーションを持っているため、初期値でtrueにしてしまう
  },
  methods: {
    handleCheck() {
      this.$emit('check', this.internalValue);
    },
    // NOTE: BInputが小文字化した直後は親のpropsへの反映前なので、BInputから渡された最新値を使う
    handleCheckWithValue(_event, value) {
      this.$emit('check', value === undefined ? this.internalValue : value);
    },
    handleClose() {
      this.$emit('close');
    },
    handleBlur(_event, value) {
      if (!this.isSettleByBlur) return;
      this.handleCheckWithValue(_event, value);
    },
  },
};
</script>

<style lang="scss" scoped>
  .b-input-box-vert {
    .input-area {
      margin-bottom: $basespace-200;
    }

    .btn-area {
      display: flex;
      justify-content: flex-end
    }
  }

  .b-input-box-horiz {
    display: flex;
    // 入力欄の下に補足文などが出てもボタンの位置が動かないよう、入力欄の高さ(40px)の中で中央揃えにする
    align-items: flex-start;

    .btn-area {
      display: flex;
      align-items: center;
      height: 40px;
      margin-left: $basespace-200;
    }
  }

  .check-icon {
    color: $basecolor-primary;
  }

  .close-icon {
    color: $textcolor-light;
  }

  .number-input {
    width: 100%;
    height: 40px;
    line-height: 40px;
    margin-right: 12px;
    &::placeholder {
      color: $concrete-dark;
    }
  }
</style>
